import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';

import { fToNow } from 'src/utils/format-time';

import { Iconify } from 'src/components/iconify';

import { getIconConfig } from './icon-config';

// ----------------------------------------------------------------------

export function NotificationItem({ notification, onMarkAsRead, onDelete, onItemClick }) {
  const { id, type, title, message, createdAt, isRead } = notification;

  const handleItemClick = () => {
    onItemClick?.(notification);
  };

  const handleDelete = (event) => {
    event.stopPropagation(); // Prevent triggering item click
    onDelete?.(id);
  };

  const renderAvatar = () => {
    const config = getIconConfig(notification.type);

    return (
      <Avatar
        sx={{
          width: 48,
          height: 48,
          color: config.color,
          bgcolor: config.bgcolor,
        }}
      >
        <Iconify icon={config.icon} width={24} />
      </Avatar>
    );
  };

  const isUnRead = !isRead;

  return (
    <ListItemButton
      disableRipple
      onClick={handleItemClick}
      sx={{
        p: 2.5,
        alignItems: 'flex-start',
        borderBottom: (theme) => `dashed 1px ${theme.vars.palette.divider}`,
        ...(isUnRead && {
          bgcolor: 'action.selected',
        }),
      }}
    >
      {renderAvatar()}

      <Stack spacing={1} flexGrow={1} sx={{ ml: 2, minWidth: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2" noWrap sx={{ flex: 1 }}>
            {title}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            {isUnRead && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  bgcolor: 'info.main',
                  borderRadius: '50%',
                }}
              />
            )}
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={handleDelete}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'error.main',
                    bgcolor: 'error.lighter',
                  },
                }}
              >
                <Iconify icon="solar:trash-bin-trash-bold" width={18} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {message}
        </Typography>

        <Typography variant="caption" color="text.disabled">
          {fToNow(createdAt)}
        </Typography>
      </Stack>
    </ListItemButton>
  );
}

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.oneOf(['Info', 'Success', 'Warning', 'Error']),
    title: PropTypes.string,
    message: PropTypes.string,
    createdAt: PropTypes.string,
    isRead: PropTypes.bool,
  }),
  onMarkAsRead: PropTypes.func,
  onDelete: PropTypes.func,
  onItemClick: PropTypes.func,
};
